/**
 * One row of the pool standings board. It renders rank, plate, name, status
 * pill, lives caption, wins and points, and carries the own row and deleted
 * account treatments. It takes a model and holds no query.
 *
 * With `onOpen` the row opens that member's rounds (ISSUE-1318) from anywhere
 * on it, matching the legacy standings rows, which open the player sheet from
 * anywhere on the row. Without it the row stays plain text. A deleted
 * account's row never opens: there is no member left to show.
 *
 * The NAME is also a link (ISSUE-1377): another member's name opens their
 * profile, as tapping a player did in v1's standings. Your own row and a
 * deleted account's row carry plain text, because the model gives them no
 * `profileAccountId`.
 *
 * A link may not sit inside a button, so an openable row is a full-size
 * button laid UNDER the row's content (the stretched button pattern): the
 * content ignores the pointer and hands every tap to the button, except the
 * name link, which takes its own taps.
 *
 * The stats line under the name (pill, lives, medals, revives) wraps whole
 * items onto a second line on a narrow phone (ISSUE-1348). Each item keeps its
 * words together, so the line never squeezes into one word per line, and the
 * player cell takes only the width left of the fixed wins and points columns.
 * Below sm the row gap is 8 instead of 12 (the sort header matches), which
 * gives the player cell 80px at 320 so 'Revived' and '10 lives left' fit
 * whole. Below sm the status pill's side padding is 4 instead of 8, so the
 * widest word, 'Eliminated', fits whole in that 80px cell too (ISSUE-1348
 * round 5). The own row's left rule is an inset shadow, not a border, so it
 * takes no width from the cell and the row's columns stay under the sort
 * header's. An item still wider than the cell shrinks to the cell and ends in
 * an ellipsis, so it never runs into the wins column.
 */
import { cn, FOCUS_RING } from "@sportsmet/ui";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import {
  STANDINGS_YOU_TAG,
  type typeStandingsRowModel,
  medalsCaptionFor,
  revivesCaptionFor,
  winsCellFor,
} from "./poolStandingsModel";

export const PoolStandingsRow = ({
  row,
  onOpen,
}: {
  row: typeStandingsRowModel;
  onOpen?: ((row: typeStandingsRowModel) => void) | undefined;
}): ReactNode => {
  const isOpenable = onOpen !== undefined && !row.isDeletedAccount;
  const body = (
    <>
      <span
        className="w-6 shrink-0 text-caption tabular-nums text-ink-tertiary"
        data-testid={`standings-row-rank-${row.entryId}`}
      >
        {row.rank}
      </span>
      {row.isDeletedAccount ? (
        <span
          className="flex size-8 shrink-0 items-center justify-center rounded-full bg-hairline"
          data-testid="standings-row-blank-plate"
        />
      ) : row.avatarUrl !== null ? (
        <img
          src={row.avatarUrl}
          alt=""
          data-testid="standings-row-avatar"
          className="size-8 shrink-0 rounded-full object-cover"
        />
      ) : (
        <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-surface-elevated text-caption text-ink">
          {row.initials}
        </span>
      )}
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="break-words text-body text-ink">
          {row.profileAccountId === null ? (
            row.displayName
          ) : (
            <Link
              to="/fan"
              search={{ accountId: row.profileAccountId }}
              className={cn(
                "pointer-events-auto relative underline-offset-4 hover:underline",
                FOCUS_RING,
              )}
              data-testid={`standings-row-profile-${row.entryId}`}
            >
              {row.displayName}
            </Link>
          )}
          {row.isViewer ? (
            <span
              className="text-caption text-ink-secondary"
              data-testid="standings-row-you"
            >
              {" "}
              {STANDINGS_YOU_TAG}
            </span>
          ) : null}
        </span>
        <span
          className="flex flex-wrap items-center gap-x-2 gap-y-1"
          data-testid={`standings-row-stats-${row.entryId}`}
        >
          <span
            className="min-w-0 max-w-full truncate rounded-full border border-hairline px-1 py-1 text-caption text-ink sm:px-2"
            data-testid={`standings-row-pill-${row.entryId}`}
          >
            {row.statusLabel}
          </span>
          {row.livesCaption === null ? null : (
            <span
              className="min-w-0 max-w-full truncate text-caption text-ink-tertiary"
              data-testid={`standings-row-lives-${row.entryId}`}
            >
              {row.livesCaption}
            </span>
          )}
          {medalsCaptionFor(row) === null ? null : (
            <span
              className="min-w-0 max-w-full truncate text-caption text-ink-tertiary"
              data-testid={`standings-row-medals-${row.entryId}`}
            >
              {medalsCaptionFor(row)}
            </span>
          )}
          {revivesCaptionFor(row) === null ? null : (
            <span
              className="min-w-0 max-w-full truncate text-caption text-ink-tertiary"
              data-testid={`standings-row-revives-${row.entryId}`}
            >
              {revivesCaptionFor(row)}
            </span>
          )}
        </span>
      </span>
      <span className="flex w-11 shrink-0 flex-col items-end text-right">
        <span
          className="text-body tabular-nums text-ink"
          data-testid={`standings-row-wins-${row.entryId}`}
        >
          {winsCellFor(row)}
        </span>
        <span className="text-caption text-ink-tertiary">wins</span>
      </span>
      <span className="flex w-11 shrink-0 flex-col items-end text-right">
        <span className="text-body tabular-nums text-ink">
          {row.pointsTotal}
        </span>
        <span className="text-caption text-ink-tertiary">pts</span>
      </span>
    </>
  );
  const rowClass = cn(
    "flex min-h-16 w-full items-center gap-2 px-4 py-2 text-left sm:gap-3",
    row.isViewer &&
      "bg-surface-elevated shadow-[inset_3px_0_0_var(--color-ink-secondary)]",
  );
  return (
    <li
      data-testid={`standings-row-${row.entryId}`}
      className="relative border-t border-hairline"
    >
      {isOpenable ? (
        <button
          type="button"
          className={cn(
            "peer absolute inset-0 size-full cursor-pointer",
            "focus-visible:outline-none",
          )}
          aria-haspopup="dialog"
          aria-label={row.displayName}
          data-testid={`standings-row-open-${row.entryId}`}
          onClick={() => {
            onOpen(row);
          }}
        />
      ) : null}
      <div
        className={cn(
          rowClass,
          isOpenable &&
            cn(
              "pointer-events-none relative",
              "transition-colors duration-200 ease-standard peer-hover:bg-surface-sunken motion-reduce:transition-none",
              "peer-focus-visible:outline-2 peer-focus-visible:-outline-offset-2 peer-focus-visible:outline-focus-ring",
            ),
        )}
      >
        {body}
      </div>
    </li>
  );
};
